// src/utils/corkBoard.js
//
// Pure helpers for the Cork Board page.
//
// Two problems live here, both of which exist because the board is a *second*
// view of data shaped for the editor rather than a model of its own:
//
//   * **chapters without a live editor.** outline.js derives chapters from the
//     contentEditable DOM, which the Cork Board does not have — it is a
//     standalone route with no editor mounted. `chaptersFromHtml` parses the
//     stored manuscript HTML into a detached document and hands that to the
//     same helpers, so both views agree on what a chapter is instead of the
//     board growing a second, drifting definition.
//
//   * **elements with no position.** Everything created from the editor's Story
//     Elements tab has no x/y, and that is normal. `layoutUnplaced` gives those
//     cards a home deterministically, so the board looks the same on every load
//     and on every collaborator's screen until someone drags something.

import { buildOutline } from './outline';
import { flattenOutline } from './storyElements';
import { wrapAsSinglePage } from './manuscriptPages';

/**
 * The chapters of a manuscript, from its stored HTML rather than a live editor.
 *
 * The outline helpers only ever call `querySelectorAll` / `getAttribute` /
 * `textContent`, so they work perfectly well on a detached element. It borrows
 * the previews' fallback via `wrapAsSinglePage`: content saved before the page
 * wrappers existed has no `.rv-page`, and `getHeadingBlocks` walks
 * `.rv-page .rv-page-body`, so unwrapped HTML would silently yield no chapters.
 *
 * @param {string} html the draft's stored `content`
 * @returns {Array<{blockId: string, text: string, level: number}>} in doc order
 */
export function chaptersFromHtml(html) {
  if (!html || typeof html !== 'string') return [];
  const host = document.createElement('div');
  host.innerHTML = html;
  if (!host.querySelector('.rv-page')) {
    host.innerHTML = wrapAsSinglePage(html);
  }
  return flattenOutline(buildOutline(host).tree).filter((c) => c.blockId);
}

// Card geometry. The board positions cards absolutely, so the stage has to know
// how big one is to keep a dragged card inside itself and to draw a string to
// its pin. Kept here rather than in the component because the clamp and the
// layout below are the things worth testing, and they both need these numbers.
export const CARD_W = 168;
export const CARD_H = 112;

// Where the pin sits on a card, as an offset from the card's top-left. Strings
// run pin-to-pin, not centre-to-centre — a string tied anywhere but the pin
// reads as a line drawn over the board rather than as thread.
//
// PIN_DY is 0 rather than a few pixels down because the pin in CorkCard.jsx is
// `top: -7px` on a 13px circle, putting its centre half a pixel *above* the
// card's top edge. These two numbers describe the same point and have to be
// changed together.
export const PIN_DX = CARD_W / 2;
export const PIN_DY = 0;

/**
 * Keep a card fully on the board.
 *
 * Clamped against the card's own size, not just its top-left corner, so a card
 * dragged at the right edge stops with its whole body visible rather than
 * hanging half off the stage.
 */
export function clampToStage(x, y, stageW, stageH) {
  const maxX = Math.max(0, (stageW || 0) - CARD_W);
  const maxY = Math.max(0, (stageH || 0) - CARD_H);
  return {
    x: Math.min(Math.max(0, Number(x) || 0), maxX),
    y: Math.min(Math.max(0, Number(y) || 0), maxY),
  };
}

// Column order and spacing for the fallback layout. Actors on the left, scenes
// in the middle, artifacts on the right — the same left-to-right reading the
// Flow Diagram uses, so switching views does not rearrange an author's mental
// map of the board.
const COLUMN_ORDER = ['actor', 'scene', 'artifact'];
const COL_GAP = 224;
const ROW_GAP = 132;
const ORIGIN_X = 48;
const ORIGIN_Y = 40;

/**
 * Give every node a position: its own if it has one, a computed one if not.
 *
 * Deterministic on purpose. The obvious alternatives are both wrong — random
 * scatter puts a card somewhere different on every reload, and defaulting to
 * (0, 0) stacks every unplaced element in one corner where the top card hides
 * the rest. Ordering by `_id` rather than array index means a board that comes
 * back from the API in a different order still lays out the same way, and Mongo
 * ObjectIds sort by creation time, so the column reads oldest-first.
 *
 * **`stageW` is why this takes an argument at all.** With fixed columns, a
 * three-type board needs about 670px, and on a phone that put the artifact
 * column past the right edge of the screen — present, reachable by scrolling,
 * and with nothing at all to say so. So the columns collapse to whatever fits:
 * as many as the stage is wide enough for, the rest wrapping onto later rows.
 * Passing nothing keeps the full-width layout, which is what the tests and any
 * caller that has not measured yet want.
 *
 * `placed: false` is carried through so the caller can tell a computed position
 * from a real one — the board saves a card's position on the first drag, and
 * nothing before it.
 *
 * @param {Array} nodes board nodes, with or without x/y
 * @param {number} [stageW] measured width of the board, if known
 * @returns {Array} `{ ...node, x, y, placed }`
 */
export function layoutUnplaced(nodes = [], stageW = 0) {
  const columns = COLUMN_ORDER.length;
  // How many columns actually fit, floored at one: a stage too narrow for even
  // one still has to put the card somewhere on it.
  const fits = stageW > 0
    ? Math.max(1, Math.floor((stageW - ORIGIN_X) / COL_GAP))
    : columns;
  // With room for every type, each gets its own column — that is what makes an
  // untouched board readable at a glance. Without room, type ordering is a
  // luxury next to being on screen, and the cards flow in sequence instead.
  const byType = fits >= columns;

  const rowInColumn = {};
  let seen = 0;

  const place = (columnIndex) => {
    const row = rowInColumn[columnIndex] || 0;
    rowInColumn[columnIndex] = row + 1;
    return {
      x: ORIGIN_X + columnIndex * COL_GAP,
      y: ORIGIN_Y + row * ROW_GAP,
    };
  };

  return [...nodes]
    .sort((a, b) => String(a._id).localeCompare(String(b._id)))
    .map((node) => {
      if (Number.isFinite(node.x) && Number.isFinite(node.y)) {
        return { ...node, placed: true };
      }
      const known = COLUMN_ORDER.indexOf(node.type);
      const columnIndex = byType
        ? (known === -1 ? columns % fits : known)   // unknown type gets the spare slot
        : (seen++ % fits);
      return { ...node, ...place(columnIndex), placed: false };
    });
}


/**
 * The strings to draw: one per relationship whose ends are both cards on this
 * board.
 *
 * Relationships pointing at a chapter are deliberately dropped. A chapter has
 * no card — chapters are not stored as elements — so there is no pin to tie the
 * other end to. They are not lost: the side panel lists every relationship an
 * element has, chapters included. This function answers "what can be drawn",
 * not "what exists".
 *
 * @param {Array} edges board edges
 * @param {Array} positioned output of `layoutUnplaced`
 * @param {string|null} selectedRef the selected card's ref, or null
 */
export function stringsFor(edges = [], positioned = [], selectedRef = null) {
  const pin = new Map(
    positioned.map((n) => [`node:${n._id}`, { x: n.x + PIN_DX, y: n.y + PIN_DY }]),
  );
  return edges.reduce((out, edge) => {
    const a = pin.get(edge.from);
    const b = pin.get(edge.to);
    if (!a || !b) return out;
    const involved = Boolean(selectedRef) &&
      (edge.from === selectedRef || edge.to === selectedRef);
    out.push({ id: String(edge._id), a, b, involved, label: edge.label || '' });
    return out;
  }, []);
}

// ── Flow diagram ─────────────────────────────────────────────────────────
//
// The board's second view: the same elements in three columns, joined by
// curves instead of thread. The geometry lives here rather than in the
// component because "which edge of which node does this curve leave from" is
// the only hard part, and it is decided from four numbers.

// How far a curve bulges out to the right when it has to join two nodes that
// sit in the same column — there is no horizontal gap to route through, so it
// leaves and re-enters on the same side.
const SAME_COLUMN_BULGE = 26;

/**
 * The cubic curve joining two measured nodes.
 *
 * Each node is given as `{ left, right, y }` in the diagram's own coordinate
 * space. A curve always leaves from the side facing its target, which is what
 * keeps it from cutting back across the node that drew it: left-to-right when
 * the target is to the right, right-to-left when it is to the left, and out
 * and back on the right when the two overlap horizontally (two actors related
 * to each other, say, which the three-column layout puts in one column).
 *
 * Control points sit on the vertical midline between the two endpoints, so the
 * curve leaves and arrives horizontally. That is what makes a bundle of them
 * read as a flow rather than as a scribble.
 *
 * @returns {{ d: string, x1: number, y1: number, x2: number, y2: number }}
 */
export function flowPath(a, b) {
  let x1;
  let x2;
  if (a.right <= b.left) {          // target is to the right
    x1 = a.right;
    x2 = b.left;
  } else if (b.right <= a.left) {   // target is to the left
    x1 = a.left;
    x2 = b.right;
  } else {                          // same column: out and back
    x1 = a.right;
    x2 = b.right + SAME_COLUMN_BULGE;
  }
  const midX = (x1 + x2) / 2;
  return {
    d: `M${x1},${a.y} C${midX},${a.y} ${midX},${b.y} ${x2},${b.y}`,
    x1, y1: a.y, x2, y2: b.y,
  };
}

/**
 * The curves to draw, given every node's measured box.
 *
 * Unlike `stringsFor`, this keeps relationships that point at a chapter: the
 * flow diagram has a chapter column, because a chapter is a thing an element
 * flows into even though it is not a card you can pin. `measured` is therefore
 * keyed by reference string rather than by node id.
 *
 * @param {Array} edges board edges
 * @param {Map<string, {left,right,y}>} measured ref -> box
 * @param {string|null} selectedRef
 */
export function flowLines(edges = [], measured = new Map(), selectedRef = null) {
  return edges.reduce((out, edge) => {
    const a = measured.get(edge.from);
    const b = measured.get(edge.to);
    if (!a || !b) return out;
    const involved = Boolean(selectedRef) &&
      (edge.from === selectedRef || edge.to === selectedRef);
    out.push({ id: String(edge._id), ...flowPath(a, b), involved, label: edge.label || '' });
    return out;
  }, []);
}
